'use client';

import type { Doc } from '@/convex/_generated/dataModel';
import { api } from '@/lib/convex';
import { usePaginatedQuery } from '@/lib/server-seed/seeded-convex-hooks';
import { useWorkspaceRouteAuth } from '@/providers/protected-workspace-boundary';
import { useParams } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';

import {
  ROOT_THREADS_FIRST_PAGE_SIZE,
  ROOT_THREADS_PAGE_SIZE,
} from '@/lib/sidebar-page-sizes';

export { ROOT_THREADS_PAGE_SIZE };

type RootThreadsPaginationStatus =
  | 'LoadingFirstPage'
  | 'CanLoadMore'
  | 'LoadingMore'
  | 'Exhausted';

interface OrgThreadSidebarPaginationValue {
  orgSlug: string;
  rootThreads: Array<Doc<'orgThreads'>>;
  rootThreadsPaginationStatus: RootThreadsPaginationStatus;
  loadMoreRootThreads: (numItems: number) => void;
  visibleRootThreadCount: number;
  setVisibleRootThreadCount: Dispatch<SetStateAction<number>>;
}

const OrgThreadSidebarPaginationContext =
  createContext<OrgThreadSidebarPaginationValue | null>(null);

const getRouteOrgSlug = (value: string | Array<string> | undefined): string => {
  if (typeof value === 'string') return value;
  return Array.isArray(value) && typeof value[0] === 'string' ? value[0] : '';
};

export const OrgThreadSidebarPaginationProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactNode => {
  const params = useParams<{ orgSlug?: string | Array<string> }>();
  const orgSlug = getRouteOrgSlug(params.orgSlug);
  const { isAuthenticated } = useWorkspaceRouteAuth();
  const {
    results: rootThreads,
    status: rootThreadsPaginationStatus,
    loadMore: loadMoreRootThreads,
  } = usePaginatedQuery(
    api.organizations.threads.queries.listRootByOrgSlugPaginated,
    isAuthenticated && orgSlug ? { orgSlug } : 'skip',
    { initialNumItems: ROOT_THREADS_FIRST_PAGE_SIZE }
  );
  const [visibleRootThreadCountsByOrg, setVisibleRootThreadCountsByOrg] =
    useState<Record<string, number>>({});
  const visibleRootThreadCount =
    visibleRootThreadCountsByOrg[orgSlug] ?? ROOT_THREADS_PAGE_SIZE;
  const setVisibleRootThreadCount = useCallback<
    Dispatch<SetStateAction<number>>
  >(
    (nextCount) => {
      if (!orgSlug) return;
      setVisibleRootThreadCountsByOrg((previous) => {
        const current = previous[orgSlug] ?? ROOT_THREADS_PAGE_SIZE;
        const next =
          typeof nextCount === 'function' ? nextCount(current) : nextCount;
        if (next === current) return previous;
        return { ...previous, [orgSlug]: next };
      });
    },
    [orgSlug]
  );
  const value = useMemo<OrgThreadSidebarPaginationValue>(
    () => ({
      orgSlug,
      rootThreads,
      rootThreadsPaginationStatus,
      loadMoreRootThreads,
      visibleRootThreadCount,
      setVisibleRootThreadCount,
    }),
    [
      loadMoreRootThreads,
      orgSlug,
      rootThreads,
      rootThreadsPaginationStatus,
      setVisibleRootThreadCount,
      visibleRootThreadCount,
    ]
  );

  return (
    <OrgThreadSidebarPaginationContext.Provider value={value}>
      {children}
    </OrgThreadSidebarPaginationContext.Provider>
  );
};

export const useOrgThreadSidebarPagination = (
  orgSlug: string
): OrgThreadSidebarPaginationValue => {
  const sharedPagination = useContext(OrgThreadSidebarPaginationContext);
  const hasMatchingSharedPagination = sharedPagination?.orgSlug === orgSlug;
  const {
    results: localRootThreads,
    status: localRootThreadsPaginationStatus,
    loadMore: loadMoreLocalRootThreads,
  } = usePaginatedQuery(
    api.organizations.threads.queries.listRootByOrgSlugPaginated,
    !hasMatchingSharedPagination && orgSlug ? { orgSlug } : 'skip',
    { initialNumItems: ROOT_THREADS_FIRST_PAGE_SIZE }
  );
  const [localVisibleRootThreadCount, setLocalVisibleRootThreadCount] =
    useState(ROOT_THREADS_PAGE_SIZE);

  if (hasMatchingSharedPagination) {
    return sharedPagination;
  }

  return {
    orgSlug,
    rootThreads: localRootThreads,
    rootThreadsPaginationStatus: localRootThreadsPaginationStatus,
    loadMoreRootThreads: loadMoreLocalRootThreads,
    visibleRootThreadCount: localVisibleRootThreadCount,
    setVisibleRootThreadCount: setLocalVisibleRootThreadCount,
  };
};
